import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";
import {
    X,
    Download,
    Send,
    Bed,
    Bath,
    Square,
    MapPin,
    Phone,
    Mail,
    Check,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Home,
    User,
    Users,
    Truck,
    AlertCircle,
} from "lucide-react";
import {
    POSTCARD_TEMPLATES,
    POSTCARD_SIZES,
    POSTCARD_PRICING,
    DEFAULT_POSTCARD_TEMPLATE,
    DEFAULT_POSTCARD_SIZE,
    getPostcardTemplate,
    getPostcardSize,
} from "../config/propertyPostcardTemplates";

/**
 * Decode over-encoded S3 URLs (e.g., %252540 -> %2540 -> %40 -> @)
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== "string") return url;
    let decoded = url;
    let prev = "";
    while (decoded !== prev && decoded.includes("%25")) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

const getPhotoUrl = (photo) => {
    return (
        photo?.processedUrl ||
        photo?.processed_url ||
        photo?.stagedUrl ||
        photo?.staged_url ||
        photo?.originalUrl ||
        photo?.original_url ||
        photo?.url ||
        null
    );
};

const formatDisplayPrice = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    return trimmed.startsWith("$") ? trimmed : `$${trimmed}`;
};

const formatDisplaySquareFeet = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim().toLowerCase();
    if (trimmed.includes("sq")) return value;
    return `${value} sq ft`;
};

const loadImageAsDataUrl = (url) =>
    new Promise((resolve) => {
        if (!url) {
            resolve(null);
            return;
        }
        if (url.startsWith("data:")) {
            resolve(url);
            return;
        }
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                const context = canvas.getContext("2d");
                context.drawImage(image, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            } catch (error) {
                console.warn("[Postcard] Failed to convert image:", error.message);
                resolve(null);
            }
        };
        image.onerror = () => resolve(null);
        const cacheBustUrl = url.includes("?")
            ? `${url}&_cors=${Date.now()}`
            : `${url}?_cors=${Date.now()}`;
        image.src = cacheBustUrl;
    });

const PropertyPostcardBuilderModal = ({
    isOpen,
    onClose,
    property,
    initialBranding,
    onSendPostcard,
    onSendToMailingList, // NEW: Callback for bulk mailing list sending
}) => {
    const postcardRef = useRef(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState(DEFAULT_POSTCARD_TEMPLATE);
    const [selectedSizeId, setSelectedSizeId] = useState(DEFAULT_POSTCARD_SIZE);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [sendSuccess, setSendSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState("design"); // design | recipient
    const [viewSide, setViewSide] = useState("front"); // front | back
    const [postcardPhotos, setPostcardPhotos] = useState([]);
    const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState({});
    const [resolvedAgentPhoto, setResolvedAgentPhoto] = useState(null);

    // Form data for property and agent
    const [formData, setFormData] = useState({
        propertyName: "",
        propertyAddress: "",
        listingPrice: "",
        bedrooms: "",
        bathrooms: "",
        squareFeet: "",
        propertyDescription: "",
        agentName: "",
        agentTitle: "Real Estate Advisor",
        agentPhone: "",
        agentEmail: "",
        agentPhoto: null,
        brokerageName: "",
        accentColor: "",
    });

    // Recipient address for mailing
    const [recipientAddress, setRecipientAddress] = useState({
        name: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: "",
    });

    // Initialize form data from property
    useEffect(() => {
        if (!property) return;

        const propInfo = property.property || property.propertyInfo || {};

        let propName =
            property.propertyName ||
            property.property_name ||
            property.name ||
            property.title ||
            propInfo.name ||
            propInfo.propertyName ||
            "";
        let propAddress =
            property.propertyAddress ||
            property.property_address ||
            property.address ||
            propInfo.address ||
            propInfo.propertyAddress ||
            "";

        if (!propAddress && propName && /\d+.*,/.test(propName)) {
            propAddress = propName;
        }
        if (!propName && propAddress) {
            propName = propAddress;
        }

        let price =
            initialBranding?.listingPrice ||
            property.listingPrice ||
            property.listing_price ||
            property.price ||
            propInfo.price ||
            propInfo.listingPrice ||
            "";
        let beds =
            initialBranding?.bedrooms ||
            property.bedrooms ||
            property.beds ||
            propInfo.bedrooms ||
            propInfo.beds ||
            "";
        let baths =
            initialBranding?.bathrooms ||
            property.bathrooms ||
            property.baths ||
            propInfo.bathrooms ||
            propInfo.baths ||
            "";
        let sqft =
            initialBranding?.squareFeet ||
            property.squareFeet ||
            property.square_feet ||
            property.sqft ||
            propInfo.squareFeet ||
            propInfo.sqft ||
            "";
        let description =
            initialBranding?.propertyDescription ||
            property.propertyDescription ||
            property.property_description ||
            property.description ||
            propInfo.description ||
            propInfo.propertyDescription ||
            "";

        const formattedPrice =
            typeof price === "number" ? price.toLocaleString() : price || "";
        const formattedSqft =
            typeof sqft === "number" ? sqft.toLocaleString() : sqft || "";
        const decodedAgentPhoto = decodeS3Url(initialBranding?.agentPhoto) || null;

        setFormData({
            propertyName: propName,
            propertyAddress: propAddress,
            listingPrice: formattedPrice,
            bedrooms: String(beds || ""),
            bathrooms: String(baths || ""),
            squareFeet: formattedSqft,
            propertyDescription:
                description ||
                "Beautifully staged and move-in ready. Schedule your private showing today!",
            agentName: initialBranding?.agentName || "",
            agentTitle: initialBranding?.agentTitle || "Real Estate Advisor",
            agentPhone: initialBranding?.agentPhone || "",
            agentEmail: initialBranding?.agentEmail || "",
            agentPhoto: decodedAgentPhoto,
            brokerageName: initialBranding?.brokerageName || "",
            accentColor: initialBranding?.accentColor || "",
        });
    }, [property, initialBranding]);

    // Extract photos from property
    const photos = useMemo(() => {
        return (property?.photos || [])
            .map((photo, index) => ({
                id: photo.photoId || photo.photo_id || photo.id || index,
                room: photo.roomType || photo.room || "Room",
                url: getPhotoUrl(photo),
            }))
            .filter((photo) => photo.url);
    }, [property]);

    useEffect(() => {
        setPostcardPhotos(photos);
    }, [photos]);

    // Resolve photo URLs to data URLs for export
    useEffect(() => {
        let isCancelled = false;
        const resolvePhotos = async () => {
            if (!postcardPhotos.length) {
                setResolvedPhotoUrls({});
                return;
            }
            const resolvedEntries = await Promise.all(
                postcardPhotos.map(async (photo) => {
                    const resolvedUrl = await loadImageAsDataUrl(photo.url);
                    return [photo.id, resolvedUrl || photo.url];
                })
            );
            if (!isCancelled) {
                setResolvedPhotoUrls(Object.fromEntries(resolvedEntries));
            }
        };
        resolvePhotos();
        return () => {
            isCancelled = true;
        };
    }, [postcardPhotos]);

    // Resolve agent photo
    useEffect(() => {
        const resolveAgentPhoto = async () => {
            if (formData.agentPhoto) {
                const resolved = await loadImageAsDataUrl(formData.agentPhoto);
                setResolvedAgentPhoto(resolved || formData.agentPhoto);
            } else {
                setResolvedAgentPhoto(null);
            }
        };
        resolveAgentPhoto();
    }, [formData.agentPhoto]);

    const selectedTemplate = getPostcardTemplate(selectedTemplateId);
    const selectedSize = getPostcardSize(selectedSizeId);
    const accentColor = formData.accentColor || selectedTemplate.accentColor;
    const headline = formData.propertyName || "Beautiful Property";
    const pricing = POSTCARD_PRICING[selectedSizeId];

    const getResolvedPhotoUrl = (photo) => {
        if (!photo) return null;
        return resolvedPhotoUrls[photo.id] || photo.url;
    };

    const heroPhoto = getResolvedPhotoUrl(postcardPhotos[0]);
    const secondaryPhoto = getResolvedPhotoUrl(postcardPhotos[1]) || heroPhoto;
    const tertiaryPhoto = getResolvedPhotoUrl(postcardPhotos[2]) || secondaryPhoto;

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const updateRecipient = (field, value) => {
        setRecipientAddress((prev) => ({ ...prev, [field]: value }));
    };

    const movePostcardPhoto = (fromIndex, toIndex) => {
        setPostcardPhotos((prev) => {
            if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            const [movedPhoto] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, movedPhoto);
            return next;
        });
    };

    const setHeroPostcardPhoto = (photoId) => {
        setPostcardPhotos((prev) => {
            const targetIndex = prev.findIndex((photo) => photo.id === photoId);
            if (targetIndex <= 0) return prev;
            const next = [...prev];
            const [targetPhoto] = next.splice(targetIndex, 1);
            next.unshift(targetPhoto);
            return next;
        });
    };

    const handleDownloadImage = async () => {
        if (!postcardRef.current) return;
        setIsDownloading(true);
        try {
            const element = postcardRef.current;
            const rect = element.getBoundingClientRect();
            const clone = element.cloneNode(true);
            clone.style.position = "absolute";
            clone.style.left = "-9999px";
            clone.style.top = "0";
            clone.style.width = `${rect.width}px`;
            clone.style.height = `${rect.height}px`;
            document.body.appendChild(clone);

            const images = clone.querySelectorAll("img");
            await Promise.all(
                Array.from(images).map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) resolve();
                            else {
                                img.onload = resolve;
                                img.onerror = resolve;
                            }
                        })
                )
            );

            const targetWidth = 2400;
            const scale = targetWidth / rect.width;

            const canvas = await html2canvas(clone, {
                scale: scale,
                useCORS: true,
                allowTaint: false,
                logging: false,
                backgroundColor: "#ffffff",
                imageTimeout: 30000,
                width: rect.width,
                height: rect.height,
            });

            document.body.removeChild(clone);

            const link = document.createElement("a");
            link.download = `${(headline || "property-postcard")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}-${selectedSizeId}.png`;
            link.href = canvas.toDataURL("image/png", 1.0);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Image export failed:", error);
            alert("Image export failed. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    const validateRecipientAddress = () => {
        const { name, addressLine1, city, state, zip } = recipientAddress;
        if (!name || !addressLine1 || !city || !state || !zip) {
            return "Please fill in all required address fields";
        }
        if (state.length !== 2) {
            return "State must be a 2-letter code (e.g., TX, CA)";
        }
        if (zip.length < 5) {
            return "ZIP code must be at least 5 characters";
        }
        return null;
    };

    const handleSendPostcard = async () => {
        setSendError(null);
        setSendSuccess(null);

        const validationError = validateRecipientAddress();
        if (validationError) {
            setSendError(validationError);
            setActiveTab("recipient");
            return;
        }

        setIsSending(true);
        try {
            const payload = {
                template_id: selectedTemplateId,
                size: selectedSizeId,
                property_data: {
                    address: formData.propertyAddress,
                    price: formatDisplayPrice(formData.listingPrice),
                    beds: formData.bedrooms,
                    baths: formData.bathrooms,
                    sqft: formatDisplaySquareFeet(formData.squareFeet),
                    headline: headline,
                    description: formData.propertyDescription,
                },
                agent_data: {
                    name: formData.agentName,
                    title: formData.agentTitle,
                    phone: formData.agentPhone,
                    email: formData.agentEmail,
                    photo_url: formData.agentPhoto,
                    brokerage: formData.brokerageName,
                },
                photo_url: postcardPhotos[0]?.url || null,
                secondary_photos: postcardPhotos.slice(1, 4).map((p) => p.url),
                to_address: {
                    name: recipientAddress.name,
                    address_line1: recipientAddress.addressLine1,
                    address_line2: recipientAddress.addressLine2 || undefined,
                    address_city: recipientAddress.city,
                    address_state: recipientAddress.state.toUpperCase(),
                    address_zip: recipientAddress.zip,
                },
                description: `Property postcard: ${formData.propertyAddress}`,
            };

            if (onSendPostcard) {
                const result = await onSendPostcard(payload);
                setSendSuccess({
                    id: result.id,
                    expectedDelivery: result.expected_delivery_date,
                });
            } else {
                // Default API call
                const response = await fetch("/api/direct-mail/property-postcards/send", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.detail || "Failed to send postcard");
                }

                const result = await response.json();
                setSendSuccess({
                    id: result.id,
                    expectedDelivery: result.expected_delivery_date,
                });
            }
        } catch (error) {
            console.error("Error sending postcard:", error);
            setSendError(error.message || "Failed to send postcard. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    // Postcard Front Preview Component
    const PostcardFront = () => {
        const isDark = selectedTemplate.isDark;
        const textPrimary = isDark ? "text-white" : "text-slate-900";
        const textSecondary = isDark ? "text-white/70" : "text-slate-600";

        const agentPhotoUrl = resolvedAgentPhoto || formData.agentPhoto;
        const initials = formData.agentName
            ? formData.agentName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
            : "A";

        return (
            <div
                className={`relative h-full w-full overflow-hidden bg-gradient-to-br ${selectedTemplate.bgGradient}`}
                style={{ fontFamily: "'Poppins', sans-serif" }}
            >
                {/* Hero Photo */}
                <div className="absolute inset-0">
                    {heroPhoto ? (
                        <img
                            src={heroPhoto}
                            alt=""
                            crossOrigin="anonymous"
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className={`h-full w-full ${isDark ? "bg-slate-800" : "bg-slate-200"}`} />
                    )}
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                </div>

                {/* Bottom bar with stats and agent */}
                <div
                    className={`absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-3 ${
                        isDark ? "bg-black/80" : "bg-white/95"
                    } backdrop-blur-sm`}
                    style={{ zIndex: 10 }}
                >
                    {/* Stats */}
                    <div className="flex items-center gap-4">
                        {formData.bedrooms && (
                            <div className={`flex items-center gap-1 text-xs ${textPrimary}`}>
                                <Bed className="h-3 w-3" style={{ color: accentColor }} />
                                <span>
                                    <strong>{formData.bedrooms}</strong> Beds
                                </span>
                            </div>
                        )}
                        {formData.bathrooms && (
                            <div className={`flex items-center gap-1 text-xs ${textPrimary}`}>
                                <Bath className="h-3 w-3" style={{ color: accentColor }} />
                                <span>
                                    <strong>{formData.bathrooms}</strong> Baths
                                </span>
                            </div>
                        )}
                        {formData.squareFeet && (
                            <div className={`flex items-center gap-1 text-xs ${textPrimary}`}>
                                <Square className="h-3 w-3" style={{ color: accentColor }} />
                                <span>
                                    <strong>{formatDisplaySquareFeet(formData.squareFeet)}</strong>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Agent */}
                    <div className="flex items-center gap-2">
                        <div className="text-right">
                            <p className={`text-xs font-semibold ${textPrimary}`}>
                                {formData.agentName || "Agent Name"}
                            </p>
                            <p className={`text-[10px] ${textSecondary}`}>
                                {formData.agentPhone}
                            </p>
                        </div>
                        {agentPhotoUrl ? (
                            <img
                                src={agentPhotoUrl}
                                alt=""
                                crossOrigin="anonymous"
                                className="h-10 w-10 rounded-full border-2 object-cover"
                                style={{ borderColor: accentColor }}
                            />
                        ) : (
                            <div
                                className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white"
                                style={{ backgroundColor: accentColor }}
                            >
                                {initials}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content overlay - positioned above the bottom bar */}
                <div className="absolute bottom-14 left-0 right-0 p-5">
                    {/* Badge */}
                    <div
                        className="mb-2 inline-block rounded px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg"
                        style={{ backgroundColor: accentColor }}
                    >
                        {selectedTemplate.badge}
                    </div>

                    {/* Headline */}
                    <h1
                        className="mb-1 text-2xl font-bold text-white drop-shadow-lg"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}
                    >
                        {headline}
                    </h1>

                    {/* Address */}
                    <p className="mb-2 flex items-center gap-1 text-sm text-white drop-shadow-md" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
                        <MapPin className="h-3 w-3" />
                        {formData.propertyAddress || "Property Address"}
                    </p>

                    {/* Price */}
                    {formData.listingPrice && (
                        <p className="text-xl font-bold drop-shadow-lg" style={{ color: accentColor, textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                            {formatDisplayPrice(formData.listingPrice)}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    // Postcard Back Preview Component
    const PostcardBack = () => {
        const agentPhotoUrl = resolvedAgentPhoto || formData.agentPhoto;
        const initials = formData.agentName
            ? formData.agentName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
            : "A";

        return (
            <div
                className="relative h-full w-full overflow-hidden bg-white"
                style={{ fontFamily: "'Poppins', sans-serif" }}
            >
                {/* Main content area */}
                <div className="flex h-full">
                    {/* Left side - Message area */}
                    <div className="flex w-1/2 flex-col justify-between border-r border-slate-200 p-6">
                        {/* Agent branding */}
                        <div>
                            {/* Agent photo and info */}
                            <div className="mb-6 flex items-center gap-3">
                                {agentPhotoUrl ? (
                                    <img
                                        src={agentPhotoUrl}
                                        alt=""
                                        crossOrigin="anonymous"
                                        className="h-14 w-14 rounded-full border-2 object-cover"
                                        style={{ borderColor: accentColor }}
                                    />
                                ) : (
                                    <div
                                        className="flex h-14 w-14 items-center justify-center rounded-full text-sm font-bold text-white"
                                        style={{ backgroundColor: accentColor }}
                                    >
                                        {initials}
                                    </div>
                                )}
                                <div>
                                    <p className="text-base font-bold text-slate-900">
                                        {formData.agentName || "Agent Name"}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                        {formData.agentTitle || "Real Estate Advisor"}
                                    </p>
                                    <p className="text-xs font-medium" style={{ color: accentColor }}>
                                        {formData.brokerageName}
                                    </p>
                                </div>
                            </div>

                            {/* Message */}
                            <div className="mb-4">
                                <p className="mb-2 text-sm font-semibold" style={{ color: accentColor }}>
                                    Dear Neighbor,
                                </p>
                                <p className="text-sm leading-relaxed text-slate-700">
                                    {formData.propertyDescription ||
                                        "This beautiful property just hit the market! Don't miss this opportunity to own your dream home in this amazing neighborhood. Schedule your private showing today!"}
                                </p>
                            </div>

                            {/* CTA */}
                            <div
                                className="inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white"
                                style={{ backgroundColor: accentColor }}
                            >
                                Schedule a Showing
                            </div>
                        </div>

                        {/* Contact info */}
                        <div className="mt-4 border-t border-slate-200 pt-4">
                            <div className="flex flex-col gap-1">
                                {formData.agentPhone && (
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Phone className="h-3.5 w-3.5" style={{ color: accentColor }} />
                                        <span className="font-medium">{formData.agentPhone}</span>
                                    </div>
                                )}
                                {formData.agentEmail && (
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Mail className="h-3.5 w-3.5" style={{ color: accentColor }} />
                                        <span>{formData.agentEmail}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right side - Mailing area */}
                    <div className="flex w-1/2 flex-col justify-between p-6">
                        {/* Return address */}
                        <div className="text-xs text-slate-600">
                            <p className="font-semibold text-slate-800">{formData.brokerageName || "Your Brokerage"}</p>
                            <p>123 Main Street</p>
                            <p>Your City, ST 12345</p>
                        </div>

                        {/* Postage area */}
                        <div className="flex justify-end">
                            <div className="flex h-16 w-20 items-center justify-center rounded border-2 border-dashed border-slate-300 bg-slate-50">
                                <div className="text-center">
                                    <p className="text-[8px] font-bold uppercase text-slate-400">Postage</p>
                                    <p className="text-[8px] uppercase text-slate-400">Indicia</p>
                                </div>
                            </div>
                        </div>

                        {/* Barcode placeholder */}
                        <div className="my-4 flex justify-center">
                            <div className="flex gap-[2px]">
                                {Array.from({ length: 30 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-slate-800"
                                        style={{
                                            width: i % 3 === 0 ? "3px" : "2px",
                                            height: i % 5 === 0 ? "24px" : "20px",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Recipient address */}
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <p className="text-sm font-bold uppercase text-slate-800">
                                {recipientAddress.name || "Recipient Name"}
                            </p>
                            <p className="text-sm uppercase text-slate-700">
                                {recipientAddress.addressLine1 || "123 Sample Street"}
                            </p>
                            {recipientAddress.addressLine2 && (
                                <p className="text-sm uppercase text-slate-700">
                                    {recipientAddress.addressLine2}
                                </p>
                            )}
                            <p className="text-sm uppercase text-slate-700">
                                {recipientAddress.city || "City"}, {recipientAddress.state || "ST"}{" "}
                                {recipientAddress.zip || "12345"}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.96, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.96, opacity: 0 }}
                    className="flex h-[92vh] w-full max-w-[1400px] overflow-hidden rounded-[28px] bg-white shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Left Panel - Settings */}
                    <div className="flex w-[400px] flex-shrink-0 flex-col border-r border-slate-200 bg-slate-50">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">
                                    Postcard Builder
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                    Create &amp; send via USPS First Class
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-slate-200">
                            <button
                                onClick={() => setActiveTab("design")}
                                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                                    activeTab === "design"
                                        ? "border-b-2 border-violet-500 text-violet-600"
                                        : "text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                <Home className="mr-2 inline h-4 w-4" />
                                Design
                            </button>
                            <button
                                onClick={() => setActiveTab("recipient")}
                                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                                    activeTab === "recipient"
                                        ? "border-b-2 border-violet-500 text-violet-600"
                                        : "text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                <User className="mr-2 inline h-4 w-4" />
                                Recipient
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            {activeTab === "design" ? (
                                <div className="space-y-4">
                                    {/* Templates */}
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Template
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {POSTCARD_TEMPLATES.map((template) => (
                                                <button
                                                    key={template.id}
                                                    onClick={() => setSelectedTemplateId(template.id)}
                                                    className={`rounded-xl border p-3 text-left transition-all ${
                                                        selectedTemplateId === template.id
                                                            ? "border-violet-500 bg-violet-50 ring-2 ring-violet-500/20"
                                                            : "border-slate-200 bg-white hover:border-violet-300"
                                                    }`}
                                                >
                                                    <div
                                                        className="mb-2 h-2 w-8 rounded-full"
                                                        style={{ backgroundColor: template.accentColor }}
                                                    />
                                                    <div className="text-sm font-semibold text-slate-900">
                                                        {template.name}
                                                    </div>
                                                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                                                        {template.useCase}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Sizes */}
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Size
                                        </label>
                                        <div className="space-y-2">
                                            {POSTCARD_SIZES.map((size) => (
                                                <button
                                                    key={size.id}
                                                    onClick={() => setSelectedSizeId(size.id)}
                                                    className={`w-full rounded-xl border p-3 text-left transition-all ${
                                                        selectedSizeId === size.id
                                                            ? "border-violet-500 bg-violet-50 ring-2 ring-violet-500/20"
                                                            : "border-slate-200 bg-white hover:border-violet-300"
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <span className="text-sm font-semibold text-slate-900">
                                                                {size.name}
                                                            </span>
                                                            {size.recommended && (
                                                                <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                                    Recommended
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-500">
                                                            {POSTCARD_PRICING[size.id]?.label}
                                                        </span>
                                                    </div>
                                                    <p className="mt-1 text-xs text-slate-500">
                                                        {size.description}
                                                    </p>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Property Details */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Property Details
                                        </label>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={formData.propertyName}
                                                onChange={(e) => updateField("propertyName", e.target.value)}
                                                placeholder="Property headline"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.propertyAddress}
                                                onChange={(e) => updateField("propertyAddress", e.target.value)}
                                                placeholder="Property address"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    value={formData.listingPrice}
                                                    onChange={(e) => updateField("listingPrice", e.target.value)}
                                                    placeholder="Price"
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                                />
                                                <input
                                                    type="text"
                                                    value={formData.squareFeet}
                                                    onChange={(e) => updateField("squareFeet", e.target.value)}
                                                    placeholder="Sq ft"
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    value={formData.bedrooms}
                                                    onChange={(e) => updateField("bedrooms", e.target.value)}
                                                    placeholder="Beds"
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                                />
                                                <input
                                                    type="text"
                                                    value={formData.bathrooms}
                                                    onChange={(e) => updateField("bathrooms", e.target.value)}
                                                    placeholder="Baths"
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Accent Color */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Accent Color
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={accentColor}
                                                onChange={(e) => updateField("accentColor", e.target.value)}
                                                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                                            />
                                            <input
                                                type="text"
                                                value={accentColor}
                                                onChange={(e) => updateField("accentColor", e.target.value)}
                                                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    {/* Photos */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                                Photos
                                            </label>
                                            <span className="text-xs text-slate-400">
                                                Hero photo is featured
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {postcardPhotos.slice(0, 4).map((photo, index) => (
                                                <div
                                                    key={photo.id}
                                                    className={`flex items-center gap-3 rounded-xl border p-2 ${
                                                        index === 0
                                                            ? "border-violet-200 bg-violet-50"
                                                            : "border-slate-200 bg-slate-50"
                                                    }`}
                                                >
                                                    <img
                                                        src={getResolvedPhotoUrl(photo)}
                                                        alt={photo.room}
                                                        crossOrigin="anonymous"
                                                        className="h-12 w-16 rounded-lg object-cover"
                                                    />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-slate-900">
                                                            {photo.room}
                                                        </p>
                                                        <p className="text-[10px] uppercase text-slate-500">
                                                            {index === 0 ? "Hero" : `Photo ${index + 1}`}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => movePostcardPhoto(index, index - 1)}
                                                            disabled={index === 0}
                                                            className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
                                                        >
                                                            <ChevronLeft className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => movePostcardPhoto(index, index + 1)}
                                                            disabled={index === postcardPhotos.length - 1}
                                                            className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
                                                        >
                                                            <ChevronRight className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Recipient Tab
                                <div className="space-y-4">
                                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Recipient Address
                                        </label>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={recipientAddress.name}
                                                onChange={(e) => updateRecipient("name", e.target.value)}
                                                placeholder="Recipient name *"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={recipientAddress.addressLine1}
                                                onChange={(e) => updateRecipient("addressLine1", e.target.value)}
                                                placeholder="Street address *"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={recipientAddress.addressLine2}
                                                onChange={(e) => updateRecipient("addressLine2", e.target.value)}
                                                placeholder="Apt, suite, unit (optional)"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={recipientAddress.city}
                                                onChange={(e) => updateRecipient("city", e.target.value)}
                                                placeholder="City *"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    value={recipientAddress.state}
                                                    onChange={(e) =>
                                                        updateRecipient("state", e.target.value.toUpperCase().slice(0, 2))
                                                    }
                                                    placeholder="State *"
                                                    maxLength={2}
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 uppercase focus:border-violet-400 focus:outline-none"
                                                />
                                                <input
                                                    type="text"
                                                    value={recipientAddress.zip}
                                                    onChange={(e) => updateRecipient("zip", e.target.value)}
                                                    placeholder="ZIP *"
                                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Agent Info */}
                                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                                            Your Info (shown on postcard)
                                        </label>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={formData.agentName}
                                                onChange={(e) => updateField("agentName", e.target.value)}
                                                placeholder="Your name"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.agentPhone}
                                                onChange={(e) => updateField("agentPhone", e.target.value)}
                                                placeholder="Phone"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.agentEmail}
                                                onChange={(e) => updateField("agentEmail", e.target.value)}
                                                placeholder="Email"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.brokerageName}
                                                onChange={(e) => updateField("brokerageName", e.target.value)}
                                                placeholder="Brokerage"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    {/* Mailing Info */}
                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                                        <div className="flex items-start gap-3">
                                            <Truck className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                                            <div>
                                                <p className="text-sm font-semibold text-emerald-900">
                                                    USPS First Class Mail
                                                </p>
                                                <p className="mt-1 text-xs text-emerald-700">
                                                    Delivered in 3-5 business days
                                                </p>
                                                <p className="mt-1 text-xs text-emerald-700">
                                                    Cost: {pricing?.label || "$0.75 + postage"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className="border-t border-slate-200 px-6 py-4 space-y-3">
                            {sendError && (
                                <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                                    <AlertCircle className="h-4 w-4" />
                                    {sendError}
                                </div>
                            )}

                            {sendSuccess && (
                                <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
                                    <div className="flex items-center gap-2 font-semibold">
                                        <Check className="h-4 w-4" />
                                        Postcard sent successfully!
                                    </div>
                                    <p className="mt-1 text-xs">
                                        ID: {sendSuccess.id}
                                        <br />
                                        Expected delivery: {sendSuccess.expectedDelivery}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleSendPostcard}
                                disabled={isSending || !!sendSuccess}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                            >
                                {isSending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : sendSuccess ? (
                                    <>
                                        <Check className="h-4 w-4" />
                                        Sent!
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4" />
                                        Send via USPS ({pricing?.label || "$0.75"})
                                    </>
                                )}
                            </button>

                            {/* Send to Mailing List button - for bulk campaigns */}
                            {onSendToMailingList && (
                                <button
                                    onClick={() => {
                                        // Get current photo URL
                                        const currentPhotoUrl =
                                            postcardPhotos.length > 0
                                                ? getPhotoUrl(postcardPhotos[0])
                                                : null;

                                        // Create postcard design payload
                                        const postcardDesign = {
                                            template_id: selectedTemplateId,
                                            size: selectedSizeId,
                                            photoUrl: currentPhotoUrl,
                                            // Note: front_html and back_html would need to be generated
                                            // For now, pass the form data so it can be generated later
                                            property_data: {
                                                address: formData.propertyAddress,
                                                price: formData.listingPrice,
                                                beds: formData.bedrooms,
                                                baths: formData.bathrooms,
                                                sqft: formData.squareFeet,
                                                description: formData.propertyDescription,
                                            },
                                            agent_data: {
                                                name: formData.agentName,
                                                title: formData.agentTitle,
                                                phone: formData.agentPhone,
                                                email: formData.agentEmail,
                                                brokerage: formData.brokerageName,
                                                photo: formData.agentPhoto,
                                            },
                                            branding: {
                                                accentColor: formData.accentColor,
                                            },
                                        };
                                        onSendToMailingList(postcardDesign);
                                    }}
                                    disabled={isSending || !!sendSuccess}
                                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                                >
                                    <Users className="h-4 w-4" />
                                    Send to Mailing List
                                </button>
                            )}

                            <button
                                onClick={handleDownloadImage}
                                disabled={isDownloading}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 disabled:opacity-50"
                            >
                                <Download className="h-4 w-4" />
                                {isDownloading ? "Exporting..." : "Download Image"}
                            </button>
                        </div>
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="flex min-w-0 flex-1 flex-col bg-[#dfe6ee]">
                        <div className="border-b border-slate-200 bg-white px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-900">
                                        {selectedTemplate.name} - {selectedSize.name}
                                    </h3>
                                    <p className="text-sm text-slate-500">
                                        {viewSide === "front" ? "Front" : "Back"} side preview ({selectedSize.widthInches}" x{" "}
                                        {selectedSize.heightInches}")
                                    </p>
                                </div>
                                <div className="flex items-center gap-4">
                                    {/* Front/Back Toggle */}
                                    <div className="flex rounded-xl border border-slate-200 bg-slate-100 p-1">
                                        <button
                                            onClick={() => setViewSide("front")}
                                            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                                                viewSide === "front"
                                                    ? "bg-white text-slate-900 shadow-sm"
                                                    : "text-slate-500 hover:text-slate-700"
                                            }`}
                                        >
                                            Front
                                        </button>
                                        <button
                                            onClick={() => setViewSide("back")}
                                            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                                                viewSide === "back"
                                                    ? "bg-white text-slate-900 shadow-sm"
                                                    : "text-slate-500 hover:text-slate-700"
                                            }`}
                                        >
                                            Back
                                        </button>
                                    </div>
                                    <div className="text-sm text-slate-500">
                                        {postcardPhotos.length} photos available
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-8">
                            <div className="mx-auto" style={{ maxWidth: "800px" }}>
                                {/* Postcard Preview */}
                                <div
                                    ref={postcardRef}
                                    className="mx-auto overflow-hidden rounded-lg shadow-[0_35px_80px_rgba(15,23,42,0.18)]"
                                    style={{
                                        aspectRatio: selectedSize.aspectRatio,
                                        width: "100%",
                                    }}
                                >
                                    {viewSide === "front" ? <PostcardFront /> : <PostcardBack />}
                                </div>

                                {/* Size indicator */}
                                <p className="mt-4 text-center text-xs text-slate-500">
                                    Actual print size: {selectedSize.widthInches}" x{" "}
                                    {selectedSize.heightInches}"
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

PropertyPostcardBuilderModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    property: PropTypes.object,
    initialBranding: PropTypes.shape({
        listingPrice: PropTypes.string,
        bedrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bathrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        squareFeet: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        propertyDescription: PropTypes.string,
        agentName: PropTypes.string,
        agentTitle: PropTypes.string,
        agentPhone: PropTypes.string,
        agentEmail: PropTypes.string,
        brokerageName: PropTypes.string,
        accentColor: PropTypes.string,
        agentPhoto: PropTypes.string,
    }),
    onSendPostcard: PropTypes.func, // Optional custom send handler
    onSendToMailingList: PropTypes.func, // Optional handler for bulk mailing list sending
};

export default PropertyPostcardBuilderModal;
